/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/mv_contains.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Checks if all values yielded by the second multivalue expression are present in the values yielded by the first multivalue expression. Returns a boolean. Null values are treated as an empty set.
 *
 * @example
 * ROW set = ["a", "b", "c"], element = "a"
 * | EVAL set_contains_element = mv_contains(set, element)
 *
 * @example
 * ROW setA = ["a","c"], setB = ["a", "b", "c"]
 * | EVAL a_subset_of_b = mv_contains(setB, setA)
 * | EVAL b_subset_of_a = mv_contains(setA, setB)
 *
 * @example
 * FROM airports
 * | WHERE mv_contains(type, ["major","military"]) AND scalerank == 9
 * | KEEP scalerank, name, country
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'mv_contains',
  signatures: [
    {
      params: [
        {
          name: 'superset',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'subset',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'subset',
          type: 'cartesian_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'subset',
          type: 'cartesian_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'date',
          optional: false,
        },
        {
          name: 'subset',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'subset',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'double',
          optional: false,
        },
        {
          name: 'subset',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'subset',
          type: 'flattened',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'subset',
          type: 'geo_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'subset',
          type: 'geo_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'subset',
          type: 'geohash',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'subset',
          type: 'geohex',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'subset',
          type: 'geotile',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'integer',
          optional: false,
        },
        {
          name: 'subset',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'ip',
          optional: false,
        },
        {
          name: 'subset',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'subset',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'subset',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'long',
          optional: false,
        },
        {
          name: 'subset',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'text',
          optional: false,
        },
        {
          name: 'subset',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'text',
          optional: false,
        },
        {
          name: 'subset',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'subset',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'superset',
          type: 'version',
          optional: false,
        },
        {
          name: 'subset',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
